import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Upload, X, Plus, FileText, Image, Video, Link as LinkIcon } from 'lucide-react';

const EnhancedTaskDialog = ({
  isOpen,
  onClose,
  taskForm,
  setTaskForm,
  editingTask,
  handleAddTask,
  handleEditTask,
  newTaskLink,
  setNewTaskLink,
  addTaskLink,
  removeTaskLink,
  handleTaskFileUpload,
  removeTaskDocument,
  uploadingTaskDocs
}) => {
  const getFileIcon = (type) => {
    if (type?.startsWith('image/')) return <Image className="h-4 w-4" />;
    if (type?.startsWith('video/')) return <Video className="h-4 w-4" />;
    return <FileText className="h-4 w-4" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingTask ? "Edit Task" : "Add New Task"}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Task Name */}
          <div>
            <Label className="text-sm font-medium text-gray-700">
              Task Name *
            </Label>
            <Input
              value={taskForm.task}
              onChange={(e) => setTaskForm(prev => ({ ...prev, task: e.target.value }))}
              placeholder="Enter task name"
              className="mt-1"
            />
          </div>

          {/* Task Description */}
          <div>
            <Label className="text-sm font-medium text-gray-700">
              Task Description
            </Label>
            <Textarea
              value={taskForm.description}
              onChange={(e) => setTaskForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe the task requirements, goals, and important details..."
              rows={4}
              className="mt-1"
            />
          </div>

          {/* Task Links */}
          <div>
            <Label className="text-sm font-medium text-gray-700">
              Task Links
            </Label>
            
            {/* Add Link */}
            <div className="flex space-x-2 mt-2">
              <Input
                value={newTaskLink}
                onChange={(e) => setNewTaskLink(e.target.value)}
                placeholder="https://example.com/resource"
                className="flex-1"
              />
              <Button
                type="button"
                onClick={addTaskLink}
                variant="outline"
                className="px-4"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>

            {/* Links List */}
            {taskForm.links.length > 0 && (
              <div className="mt-3 space-y-2">
                {taskForm.links.map((link, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-2 flex-1">
                      <LinkIcon className="h-4 w-4 text-gray-500" />
                      <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 truncate"
                      >
                        {link}
                      </a>
                    </div>
                    <Button
                      type="button"
                      onClick={() => removeTaskLink(index)}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Task Documents */}
          <div>
            <Label className="text-sm font-medium text-gray-700">
              Task Documents
            </Label>
            
            {/* Upload Area */}
            <div className="mt-2 border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
              <input
                type="file"
                multiple
                onChange={handleTaskFileUpload}
                className="hidden"
                id="task-document-upload"
                accept="image/*,video/*,.pdf,.doc,.docx,.txt"
              />
              <label
                htmlFor="task-document-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <Upload className="h-6 w-6 text-gray-400 mb-2" />
                <span className="text-sm text-gray-600">
                  Click to upload task documents
                </span>
                <span className="text-xs text-gray-400 mt-1">
                  Supports: Images, Videos, PDF, DOC, TXT
                </span>
              </label>
            </div>

            {/* Documents List */}
            {taskForm.documents.length > 0 && (
              <div className="mt-3 space-y-2">
                {taskForm.documents.map((doc, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      {getFileIcon(doc.type)}
                      <div>
                        <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(doc.size)}</p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      onClick={() => removeTaskDocument(index)}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {uploadingTaskDocs && (
              <div className="mt-2 text-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-sm text-gray-500 mt-1">Uploading files...</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              onClick={editingTask ? handleEditTask : handleAddTask}
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={!taskForm.task.trim()}
            >
              {editingTask ? "Update Task" : "Add Task"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EnhancedTaskDialog;